"use client"

/**
 * Floating Ask Leo launcher — the corner button, in two jobs.
 *
 * 1. **Products home** (`/home`, `/home/:product`). The utility bar's
 *    `AskLeoToggle` is hidden there (no active product, so most of the bar is
 *    stripped), which left a buyer with no way to ask "which of these do I
 *    actually need?" — every card can only pitch itself.
 * 2. **A minimised window.** Minimise has to park Leo somewhere visible, or the
 *    conversation is simply gone. This is that somewhere.
 *
 * Composes `AskLeoButton` rather than adding a shared FAB primitive: the ⌘⌥K
 * tooltip, the accessible name, and the star's motion states are all already
 * correct there.
 *
 * @see components/floating-marketing-banner-slot.tsx — the corner-slot precedent
 * @see components/ask-leo-window.tsx — what minimise collapses into this
 */

import * as React from "react"
import { createPortal } from "react-dom"

import { AskLeoButton } from "@/components/ask-leo-button"
import { useAskLeo } from "@/components/ask-leo-context"

/**
 * The app shell's main column, which is a flex sibling of the Ask Leo panel and
 * therefore narrows when the panel opens. Anchoring to it keeps the launcher
 * beside the panel without reading the panel's (user-resizable) width — a fixed
 * offset off the viewport would drift the moment someone dragged the panel edge.
 */
function useAppShellMain(): HTMLElement | null {
  const [host, setHost] = React.useState<HTMLElement | null>(null)
  React.useEffect(() => {
    setHost(document.querySelector<HTMLElement>("[data-app-shell-main]"))
  }, [])
  return host
}

export function LeoLauncherFab({
  /** Products home wording — names the decision the reader is actually making. */
  salesFraming = false,
}: {
  salesFraming?: boolean
}) {
  const { open, busy, minimized, setMinimized, toggle } = useAskLeo()
  const host = useAppShellMain()

  const label = minimized ? "Restore Ask Leo" : open ? "Close Ask Leo" : "Ask Leo"
  const tooltip = minimized
    ? "Restore Ask Leo. Your conversation is still here."
    : open
      ? "Close Ask Leo"
      : salesFraming
        ? "Ask Leo which product fits"
        : "Ask Leo"

  if (!host) return null

  return createPortal(
    <div
      data-slot="leo-launcher-fab"
      className="pointer-events-none absolute bottom-4 end-4 z-40 flex"
    >
      {/* Icon only. The tooltip and the accessible name carry "Ask Leo", so the
          label is one hover or one screen-reader stop away rather than
          permanently occupying the corner. */}
      <AskLeoButton
        size="lg"
        iconOnly
        introduceOnMount
        // Loops the star's `working` gesture for as long as Leo is composing an
        // answer, so a minimised window still shows that it is thinking.
        aria-busy={busy}
        ariaLabel={label}
        tooltipLabel={tooltip}
        onClick={minimized ? () => setMinimized(false) : toggle}
        // Brand fill with the on-brand star, at the 56px the platforms use for
        // a primary floating action — the 44px outline version read as a stray
        // toolbar button that had come loose.
        tone="brand"
        starSize="sm"
        className="pointer-events-auto size-14 rounded-full shadow-[var(--shadow-sheet-panel)]"
      />
    </div>,
    host,
  )
}
